import React, { useEffect, useMemo, useState } from "react";
import { useApi } from "../../context/ApiContext";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function UnitsAdmin() {
  const navigate = useNavigate();
  const {
    adminListProperties: fetchProperties,
    adminListUnits: fetchUnits,
    adminListUnitsForMany: fetchUnitsForMany,
    adminReleaseReservation,
    adminAssignUnitToApplicant,
    adminListApplicants,
    adminGetTenant,
    adminGetApplicant,
    baseUrl,
  } = useApi();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialSlug = searchParams.get("slug") || "";
  const initialPage = Number(searchParams.get("page") || 1);
  const initialSize = Number(searchParams.get("size") || 10);

  const [properties, setProperties] = useState([]);
  const [propertySlug, setPropertySlug] = useState(initialSlug);
  const [units, setUnits] = useState([]); // flattened list; includes __slug per unit
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(Number.isFinite(initialPage) && initialPage > 0 ? initialPage : 1);
  const [pageSize, setPageSize] = useState(Number.isFinite(initialSize) && initialSize > 0 ? initialSize : 10);

  // Assign modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignUnit, setAssignUnit] = useState(null); // { id, __slug, name }
  const [appsPages, setAppsPages] = useState([]); // applicants items per page
  const [appsCursors, setAppsCursors] = useState([null]); // cursor history per page
  const [appsPage, setAppsPage] = useState(1); // 1-based index
  const [appsLoading, setAppsLoading] = useState(false);
  const [appsQ, setAppsQ] = useState("");
  // Hover applicant data cache for reserved badge
  const [hoverAppId, setHoverAppId] = useState(null);
  const [hoverCache, setHoverCache] = useState({});
  // Tenant cache for occupied units
  const [tenantCache, setTenantCache] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const list = await fetchProperties();
        setProperties(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [fetchProperties]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError("");
        if (propertySlug) {
          const arr = await fetchUnits(propertySlug);
          const list = Array.isArray(arr) ? arr : [];
          setUnits(list.map((u) => ({ ...u, __slug: propertySlug })));
        } else {
          // show all units across properties
          if (!properties || properties.length === 0) {
            setUnits([]);
            return;
          }
          const slugs = properties.map((p) => p.slug).filter(Boolean);
          const map = await fetchUnitsForMany(slugs);
          const flattened = Object.entries(map || {}).flatMap(([slug, arr]) =>
            (Array.isArray(arr) ? arr : []).map((u) => ({ ...u, __slug: slug }))
          );
          setUnits(flattened);
        }
      } catch (e) {
        setError(e?.message || "Failed to load units");
      } finally {
        setLoading(false);
      }
    })();
  }, [propertySlug, properties, fetchUnits, fetchUnitsForMany]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (propertySlug) next.set("slug", propertySlug); else next.delete("slug");
    if (page > 1) next.set("page", String(page)); else next.delete("page");
    if (pageSize !== 10) next.set("size", String(pageSize)); else next.delete("size");
    setSearchParams(next, { replace: true });
  }, [propertySlug, page, pageSize]);

  // Reset to first page when filter changes
  useEffect(() => {
    setPage(1);
  }, [propertySlug]);

  // Prefetch occupant/applicant display info for the current page
  useEffect(() => {
    (async () => {
      try {
        const ids = new Set();
        const appIds = new Set();
        (units || []).forEach(u => {
          if (u.occupantTenantId) ids.add(u.occupantTenantId);
          if (u.reservedByAppId) appIds.add(u.reservedByAppId);
        });
        // fetch tenants not in cache
        for (const id of ids) {
          if (!tenantCache[id]) {
            try {
              const data = await adminGetTenant(id);
              if (data) setTenantCache(prev => ({ ...prev, [id]: data }));
            } catch {}
          }
        }
        // fetch applicants not in cache (reuse hoverCache)
        for (const id of appIds) {
          if (!hoverCache[id]) {
            try {
              const data = await adminGetApplicant(id);
              if (data) setHoverCache(prev => ({ ...prev, [id]: data }));
            } catch {}
          }
        }
      } catch {}
    })();
  }, [units]);

  async function openAssignModal(unit) {
    setAssignUnit(unit);
    setAssignModalOpen(true);
    resetAndFetchApplicants({ unit });
  }

  function resetAndFetchApplicants({ unit } = {}){
    setAppsPages([]);
    setAppsCursors([null]);
    setAppsPage(1);
    fetchApplicantsPage(1, { force: true, unit });
  }

  async function fetchApplicantsPage(pageIndex, { force = false, unit } = {}) {
    try {
      setAppsLoading(true);
      if (!force && appsPages[pageIndex - 1]) {
        setAppsPage(pageIndex);
        return;
      }
      const cursor = appsCursors[pageIndex - 1] || null;
      const res = await adminListApplicants({ limit: 25, cursor, q: appsQ || undefined, property: (unit?.__slug || propertySlug) || undefined });
      const items = Array.isArray(res) ? res : (res?.items || []);
      const next = Array.isArray(res) ? null : (res?.nextCursor || null);
      setAppsPages((prev) => {
        const copy = prev.slice();
        copy[pageIndex - 1] = items;
        return copy;
      });
      setAppsCursors((prev) => {
        const copy = prev.slice();
        copy[pageIndex] = next;
        return copy;
      });
      setAppsPage(pageIndex);
    } finally {
      setAppsLoading(false);
    }
  }

  const pageCount = useMemo(() => Math.max(1, Math.ceil((units?.length || 0) / pageSize)), [units, pageSize]);
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [pageCount]);

  const pagedUnits = useMemo(() => {
    const total = units?.length || 0;
    if (total === 0) return [];
    const start = Math.min((page - 1) * pageSize, Math.max(0, total - 1));
    const end = Math.min(start + pageSize, total);
    return units.slice(start, end);
  }, [units, page, pageSize]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Units</h1>
        <div className="flex items-center gap-2">
          <select
            className="border rounded-md px-3 py-2"
            value={propertySlug}
            onChange={(e) => setPropertySlug(e.target.value)}
          >
            <option value="">Select Property…</option>
            {properties.map((p) => (
              <option key={p.slug} value={p.slug}>
                {p.name} ({p.location})
              </option>
            ))}
          </select>
          <button
            onClick={() => propertySlug && navigate(`/admin/units/new?slug=${encodeURIComponent(propertySlug)}`)}
            disabled={!propertySlug}
            className="px-3 py-2 rounded-md border"
          >
            Add Unit
          </button>
        </div>
      </div>

      {error && <div className="text-red-600">{error}</div>}

      <div className="bg-white border rounded-lg p-4">
          <h2 className="font-semibold mb-3">Units {propertySlug ? `(Filtered)` : `(All Properties)`}</h2>
          {loading ? (
            <div>Loading…</div>
          ) : units.length === 0 ? (
            <div className="text-gray-500">No units found.</div>
          ) : (
            <div className="overflow-x-auto">
              <div className="flex items-center justify-between mb-3 text-sm">
                <div>
                  {(() => {
                    const total = units.length;
                    const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
                    const end = Math.min(page * pageSize, total);
                    return `Showing ${start}-${end} of ${total}`;
                  })()}
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-gray-600">Rows per page</label>
                  <select
                    className="border rounded-md px-2 py-1"
                    value={pageSize}
                    onChange={(e) => {
                      const next = Number(e.target.value) || 10;
                      setPageSize(next);
                      setPage(1);
                    }}
                  >
                    {[10, 25, 50, 100].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <div className="flex items-center gap-1">
                    <button
                      className="px-2 py-1 border rounded-md"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </button>
                    <span className="px-2">Page {page} of {pageCount}</span>
                    <button
                      className="px-2 py-1 border rounded-md"
                      disabled={page >= pageCount}
                      onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Property</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Beds</th>
                    <th className="text-right px-3 py-2">Price</th>
                    <th className="text-center px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Assigned To</th>
                <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedUnits.map((u) => {
                    const prop = properties.find((p) => p.slug === u.__slug);
                    return (
                     <tr key={u.id} className="border-t">
                       <td className="px-3 py-2">{u.name}</td>
                       <td className="px-3 py-2">{prop ? `${prop.name} (${prop.location})` : u.__slug}</td>
                       <td className="px-3 py-2">{u.roomTypeName || "-"}</td>
                       <td className="px-3 py-2">{u.beds}</td>
                       <td className="px-3 py-2 text-right">R{Number(u.price || 0).toLocaleString()}</td>
                       <td className="px-3 py-2 text-center relative">
                         <div className="inline-flex items-center gap-2">
                           <span className={`px-2 py-0.5 rounded text-xs ${u.available ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{u.available ? 'Available' : 'Occupied'}</span>
                           {u.reservedByAppId && (
                             <span
                               className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800 cursor-default"
                               onMouseEnter={async (e)=>{
                                 const id = u.reservedByAppId;
                                 setHoverAppId(id);
                                 if (!hoverCache[id]) {
                                   try {
                                     const data = await adminGetApplicant(id);
                                     setHoverCache(prev => ({ ...prev, [id]: data }));
                                   } catch {}
                                 }
                               }}
                               onMouseLeave={()=> setHoverAppId(null)}
                             >
                             Reserved
                             </span>
                           )}
                         </div>
                         {u.reservedByAppId && hoverAppId === u.reservedByAppId && (
                           <div className="absolute z-50 mt-1 right-0 w-72 bg-white border rounded shadow p-3 text-left">
                             {(() => {
                               const a = hoverCache[u.reservedByAppId];
                               if (!a) return <div className="text-sm text-gray-600">Loading…</div>;
                               return (
                                 <div className="text-sm">
                                   <div className="font-semibold mb-1">Applicant</div>
                                   <div className="mb-1">{a.fullName || a.form?.fullName || '—'}</div>
                                   <div className="text-gray-600 mb-1">{a.email || a.form?.email} • {a.phone || a.form?.phone}</div>
                                   <a className="text-blue-600 underline text-xs" href={`/admin/applicants/${encodeURIComponent(u.reservedByAppId)}`}>Open profile</a>
                                 </div>
                               );
                             })()}
                           </div>
                         )}
                       </td>
                       <td className="px-3 py-2 text-left">
                         {u.occupantTenantId ? (
                           (() => {
                             const t = tenantCache[u.occupantTenantId];
                             if (!t) return <span className="text-gray-500">Tenant</span>;
                             const label = t.name || (t.email || '').split('@')[0] || '';
                             const tdocs = t.documents || [];
                             const avatar = t.profilePhoto || (tdocs.find(d => typeof d?.name==='string' && /profile/i.test(d.name) && d.url)?.url) || (tdocs.find(d => (d?.fileName||d?.url||'').toLowerCase().match(/\.(jpg|jpeg|png|webp)$/) && d.url)?.url);
                             return (
                               <a className="inline-flex items-center gap-2 text-blue-600 underline" href={`/admin/tenants/${encodeURIComponent(u.occupantTenantId)}`}>
                                 {avatar ? (<img src={avatar} alt={label} className="w-5 h-5 rounded-full object-cover" />) : (<span className="w-5 h-5 rounded-full bg-gray-200 inline-block" />)}
                                 <span>{label || 'Tenant'}</span>
                               </a>
                             );
                           })()
                         ) : u.reservedByAppId ? (
                           (() => {
                             const a = hoverCache[u.reservedByAppId];
                             if (!a) return <span className="text-gray-500">Applicant</span>;
                             const label = a.fullName || a.form?.fullName || (a.email || '').split('@')[0] || '';
                             const adocs = a.documents || [];
                             const avatar = a.profilePhoto || (adocs.find(d => typeof d?.name==='string' && /profile/i.test(d.name) && d.url)?.url) || (adocs.find(d => (d?.fileName||d?.url||'').toLowerCase().match(/\.(jpg|jpeg|png|webp)$/) && d.url)?.url);
                             return (
                               <a className="inline-flex items-center gap-2 text-blue-600 underline" href={`/admin/applicants/${encodeURIComponent(u.reservedByAppId)}`}>
                                 {avatar ? (<img src={avatar} alt={label} className="w-5 h-5 rounded-full object-cover" />) : (<span className="w-5 h-5 rounded-full bg-gray-200 inline-block" />)}
                                 <span>{label || 'Applicant'}</span>
                               </a>
                             );
                           })()
                         ) : (
                          <span className="text-gray-500">—</span>
                         )}
                       </td>
                       <td className="px-3 py-2 text-right">
                         <div className="flex items-center gap-2 justify-end">
                           <button onClick={() => navigate(`/admin/units/${u.id}/edit?slug=${encodeURIComponent(u.__slug || propertySlug)}`)} className="px-2 py-1 rounded-md border">Edit</button>
                           <button
                             className="px-2 py-1 rounded-md border"
                             title={u.available && !u.reservedByAppId ? "Assign this unit to an Applicant" : (u.reservedByAppId ? 'Unit is reserved' : 'Unit is occupied')}
                             disabled={!u.available || !!u.reservedByAppId}
                             onClick={() => openAssignModal(u)}
                            >Assign to Applicant</button>
                            <button
                              className="px-2 py-1 rounded-md border"
                              onClick={() => {
                                const url = `${window.location.origin}/apply-to-rent?property=${encodeURIComponent(u.__slug || propertySlug || '')}&unit=${encodeURIComponent(u.id)}`;
                                try { navigator.clipboard?.writeText(url); } catch {}
                                alert(`Invite link copied:\n${url}`);
                              }}
                            >Invite Applicant</button>
                           {u.reservedByAppId && (
                             <button
                               className="px-2 py-1 rounded-md border"
                               onClick={async()=>{
                                 if (!window.confirm('Release reservation on this unit?')) return;
                                 try {
                                   await adminReleaseReservation(u.id);
                                   // Refresh units
                                   if (propertySlug) {
                                     const arr = await fetchUnits(propertySlug);
                                     const list = Array.isArray(arr) ? arr : [];
                                     setUnits(list.map((x) => ({ ...x, __slug: propertySlug })));
                                   } else {
                                     if (properties && properties.length > 0) {
                                       const slugs = properties.map((p) => p.slug).filter(Boolean);
                                       const map = await fetchUnitsForMany(slugs);
                                       const flattened = Object.entries(map || {}).flatMap(([slug, arr]) => (Array.isArray(arr) ? arr : []).map((x) => ({ ...x, __slug: slug })));
                                       setUnits(flattened);
                                     }
                                   }
                                 } catch (e) { alert(e?.message || 'Failed to release'); }
                               }}
                             >Release Reservation</button>
                           )}
                         </div>
                       </td>
                     </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {assignModalOpen && (
          <div className="fixed inset-0 z-40">
            <div className="absolute inset-0 bg-black/40" onClick={()=>setAssignModalOpen(false)} />
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-xl p-4 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold">Assign Unit “{assignUnit?.name || assignUnit?.id}”</div>
                <button className="border rounded px-2 py-1" onClick={()=>setAssignModalOpen(false)}>Close</button>
              </div>
              <div className="mb-3 flex items-center gap-2">
                <input className="border rounded px-3 py-2 flex-1" placeholder="Search applicants by name/email/phone" value={appsQ} onChange={(e)=>setAppsQ(e.target.value)} />
                <button className="px-3 py-2 border rounded" onClick={()=>resetAndFetchApplicants({ unit: assignUnit })}>Search</button>
              </div>
              <div className="text-sm text-gray-600 mb-2">Property filter: {assignUnit?.__slug || propertySlug || 'any'}</div>
              <div className="flex-1 overflow-auto border rounded">
                {appsLoading && (appsPages[appsPage - 1] || []).length === 0 ? (
                  <div className="p-4">Loading…</div>
                ) : (appsPages[appsPage - 1] || []).length === 0 ? (
                  <div className="p-4 text-gray-600">No applicants found.</div>
                ) : (
                  <ul>
                    {(appsPages[appsPage - 1] || []).map(a => (
                      <li key={a.id} className="p-3 border-b flex items-center justify-between">
                        <div>
                          <div className="font-medium">{a.fullName || a.form?.fullName}</div>
                          <div className="text-xs text-gray-600">{a.email || a.form?.email} • {a.phone || a.form?.phone}</div>
                          <div className="text-xs text-gray-500">Applicant ID: {a.id}</div>
                        </div>
                        <button className="px-2 py-1 border rounded" onClick={async()=>{
                          try {
                            await adminAssignUnitToApplicant({ appId: a.id, unitId: assignUnit.id });
                            alert('Assigned. Approve applicant to mark unit occupied.');
                            setAssignModalOpen(false);
                          } catch (e) { alert(e?.message || 'Failed to assign'); }
                        }}>Assign</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="mt-3 flex items-center justify-center gap-2">
                <button
                  className="px-3 py-2 border rounded"
                  disabled={appsPage === 1 || appsLoading}
                  onClick={()=> setAppsPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <span className="text-xs text-gray-700">Page {appsPage}</span>
                <button
                  className="px-3 py-2 border rounded"
                  disabled={appsLoading || (!appsPages[appsPage] && !appsCursors[appsPage])}
                  onClick={()=>{
                    const nextPage = appsPage + 1;
                    if (appsPages[nextPage - 1]) {
                      setAppsPage(nextPage);
                    } else if (appsCursors[appsPage]) {
                      fetchApplicantsPage(nextPage, { unit: assignUnit });
                    }
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
